import * as Sentry from "@sentry/react";
import type { Session, User } from "@supabase/supabase-js";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser } from "@/lib/sentry";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  emailVerified: boolean;
  verificationLoading: boolean;
  /** True when the user signed in via Google OAuth — skip verification prompts */
  isOAuthUser: boolean;
  sendVerificationEmail: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  emailVerified: false,
  verificationLoading: false,
  isOAuthUser: false,
  sendVerificationEmail: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);

  const detectOAuth = useCallback((u: User | null) => {
    if (!u) return false;
    const providers: string[] = u.app_metadata?.providers ?? [];
    // If they signed up via google/github/etc, treat as OAuth
    return providers.some((p) => ["google", "github", "apple", "azure", "facebook", "twitter"].includes(p));
  }, []);

  // Check verification status from profiles table
  const checkVerification = useCallback(async (userId: string) => {
    const { data } = await supabase.from("profiles").select("email_verified").eq("user_id", userId).maybeSingle();
    if (data) {
      setEmailVerified(!!data.email_verified);
    }
  }, []);

  // Auto-verify OAuth users and magic-link users
  const autoVerifyIfNeeded = useCallback(
    async (u: User) => {
      const isOAuth = detectOAuth(u);
      const isMagicLinkOrConfirmed = !!u.email_confirmed_at;

      if (isOAuth || isMagicLinkOrConfirmed) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("email_verified")
          .eq("user_id", u.id)
          .maybeSingle();

        if (profile && !profile.email_verified) {
          await supabase
            .from("profiles")
            .update({
              email_verified: true,
              email_verified_at: new Date().toISOString(),
            })
            .eq("user_id", u.id);
        }
        setEmailVerified(true);
        return true;
      }
      return false;
    },
    [detectOAuth],
  );

  useEffect(() => {
    let mounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        const u = session.user;
        setIsOAuthUser(detectOAuth(u));

        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          // Defer to avoid deadlock
          setTimeout(async () => {
            if (!mounted) return;
            try {
              const autoVerified = await autoVerifyIfNeeded(u);
              if (!mounted) return;
              if (!autoVerified) {
                await checkVerification(u.id);
              }
            } catch (e) {
              Sentry.captureException(e, { tags: { context: "auto-verify" } });
            }
          }, 0);
        }
      } else {
        setEmailVerified(false);
        setIsOAuthUser(false);
      }
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        setIsOAuthUser(detectOAuth(session.user));
        const autoVerified = await autoVerifyIfNeeded(session.user);
        if (!mounted) return;
        if (!autoVerified) {
          await checkVerification(session.user.id);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [checkVerification, autoVerifyIfNeeded, detectOAuth]);

  // Sync authenticated user to Sentry for error context
  useEffect(() => {
    setSentryUser(user ? { id: user.id, email: user.email } : null);
  }, [user]);

  const sendVerificationEmail = useCallback(async () => {
    if (!user?.email) return;
    setVerificationLoading(true);
    try {
      // Check rate limit from profiles
      const { data: profile } = await supabase
        .from("profiles")
        .select("verification_sent_at")
        .eq("user_id", user.id)
        .maybeSingle();

      const lastSent = profile?.verification_sent_at;
      if (lastSent) {
        const diff = Date.now() - new Date(lastSent).getTime();
        if (diff < 60_000) {
          throw new Error("Please wait a moment before requesting another verification email.");
        }
      }

      // Send OTP email (magic link for verification)
      const { error } = await supabase.auth.signInWithOtp({
        email: user.email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: window.location.href,
        },
      });
      if (error) throw error;

      // Update rate limit timestamp
      await supabase.from("profiles").update({ verification_sent_at: new Date().toISOString() }).eq("user_id", user.id);
    } finally {
      setVerificationLoading(false);
    }
  }, [user]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      emailVerified,
      verificationLoading,
      isOAuthUser,
      sendVerificationEmail,
      signOut,
    }),
    [user, session, loading, emailVerified, verificationLoading, isOAuthUser, sendVerificationEmail, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
